import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getRouteApi, Link, useNavigate } from "@tanstack/react-router";
import { AppIcon, Badge, Button, IconArrowLeft, Notice, Plate, SelectField, TextField } from "@wilco/ui";
import { useDeferredValue, useEffect, useState, type SubmitEvent } from "react";
import { serverErrorText, t } from "../i18n.ts";
import { errorNotice, type NoticeState } from "../stock/ShelfPage.tsx";
import { catalogApi, type Partner, type PartnerInput } from "./api.ts";
import { CATALOG, partnerQuery, partnersQuery } from "./queries.ts";

/** Контрагенты: поиск по названию и ЄДРПОУ. */
export function PartnersPage() {
  const [text, setText] = useState("");
  const q = useDeferredValue(text.trim());
  const list = useQuery(partnersQuery(q));
  return (
    <div className="page catalog">
      <div className="page-head">
        <h1 className="page-title page-title-icon">
          <AppIcon name="contacts" size={32} />
          {t("partners.title")}
        </h1>
        <Button asChild variant="create">
          <Link to="/partners/$id" params={{ id: "new" }}>
            {t("partners.new")}
          </Link>
        </Button>
      </div>
      <form role="search" onSubmit={(e) => e.preventDefault()}>
        <TextField label={t("partners.search")} placeholder={t("partners.searchHint")} value={text} onChange={(e) => setText(e.target.value)} autoComplete="off" />
      </form>
      {list.error ? <Notice tone="danger" title={errorNotice(list.error).title} className="stock-notice" /> : null}
      <ul className="catalog-results">
        {list.data?.map((p) => (
          <li key={p.id}>
            <Link to="/partners/$id" params={{ id: p.id }} className="catalog-result">
              <span className="catalog-result-main">
                <span>{p.name}</span>
                <span className="w-muted catalog-result-meta">
                  {[p.registryCode ? t("partners.code", { code: p.registryCode }) : p.country, p.currency, p.vatPayer ? t("partners.vat") : t("partners.noVat")]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
              </span>
              <span className="tags">
                {p.supplier ? <Badge tone="strong">{t("partners.supplier")}</Badge> : null}
                {p.customer ? <Badge>{t("partners.customer")}</Badge> : null}
                {p.archived ? <Badge>{t("catalog.archived")}</Badge> : null}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

const route = getRouteApi("/app/partners/$id");

// Итог создания переживает переход на адрес новой карточки: форма там
// монтируется заново, а предупреждение о дубле кода надо показать.
let flash: NoticeState | null = null;

const EMPTY: PartnerInput = { name: "", kind: "COMPANY", registryCode: "", country: "UA", currency: "UAH", vatPayer: true, supplier: true, customer: false, contacts: "" };

/**
 * Карточка контрагента (PRT-01, ЗК-24): украинскому поставщику ЄДРПОУ
 * (8 цифр) или РНОКПП ФОП (10), иностранному вместо кода страна. Дубль
 * кода сохраняется с предупреждением, а не отказом.
 */
export function PartnerPage() {
  const { id } = route.useParams();
  const isNew = id === "new";
  const existing = useQuery({ ...partnerQuery(id), enabled: !isNew });
  if (!isNew && !existing.data) {
    return existing.error ? <Notice tone="danger" title={errorNotice(existing.error).title} className="page" /> : null;
  }
  return <PartnerForm key={existing.data?.id ?? "new"} partner={existing.data ?? null} />;
}

function PartnerForm({ partner }: { partner: Partner | null }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<PartnerInput>(() => (partner ? { ...partner } : EMPTY));
  // В StrictMode инициализатор идёт дважды: очищает эффект, а не он.
  const [notice, setNotice] = useState<NoticeState | null>(() => flash);
  useEffect(() => {
    flash = null;
  }, []);
  const save = useMutation({ mutationFn: (body: PartnerInput) => (partner ? catalogApi.savePartner(partner.id, body) : catalogApi.createPartner(body)) });
  const archive = useMutation({ mutationFn: (v: boolean) => (v ? catalogApi.archivePartner(partner?.id ?? "") : catalogApi.restorePartner(partner?.id ?? "")) });
  const set = <K extends keyof PartnerInput>(k: K, v: PartnerInput[K]) => setForm((f) => ({ ...f, [k]: v }));

  const submit = (e: SubmitEvent) => {
    e.preventDefault();
    setNotice(null);
    save.mutate(
      { ...form, registryCode: form.registryCode?.trim() || null, country: form.country?.trim() || null, contacts: form.contacts?.trim() || null },
      {
        onSuccess: (saved) => {
          void queryClient.invalidateQueries({ queryKey: CATALOG });
          const warning = saved.warnings[0];
          const n: NoticeState = warning
            ? { tone: "warning", title: serverErrorText(warning.code, warning.args) ?? warning.code }
            : { tone: "success", title: t("partners.saved") };
          if (partner) {
            setNotice(n);
          } else {
            flash = n;
            queryClient.setQueryData(partnerQuery(saved.partner.id).queryKey, saved.partner);
            void navigate({ to: "/partners/$id", params: { id: saved.partner.id }, replace: true });
          }
        },
        onError: (err) => setNotice(errorNotice(err)),
      },
    );
  };

  return (
    <div className="page stock-narrow">
      <div className="page-head">
        <Button asChild variant="ghost" size="icon">
          <Link to="/partners" aria-label={t("partners.back")}>
            <IconArrowLeft />
          </Link>
        </Button>
        <h1 className="page-title">{partner ? partner.name : t("partners.new")}</h1>
        {partner?.archived ? <Badge>{t("catalog.archived")}</Badge> : null}
      </div>
      {notice ? <Notice tone={notice.tone} title={notice.title} className="stock-notice" /> : null}
      <Plate cut="lg">
        <form className="stock-form catalog-form" onSubmit={submit}>
          <TextField label={t("partners.name")} value={form.name} onChange={(e) => set("name", e.target.value)} required />
          <SelectField
            label={t("partners.kind")}
            value={form.kind}
            onChange={(e) => set("kind", e.target.value as PartnerInput["kind"])}
            options={[
              { value: "COMPANY", label: t("partners.kind.COMPANY") },
              { value: "PERSON", label: t("partners.kind.PERSON") },
            ]}
          />
          <TextField
            label={form.kind === "COMPANY" ? t("partners.edrpou") : t("partners.rnokpp")}
            hint={t("partners.codeHint")}
            inputMode="numeric"
            value={form.registryCode ?? ""}
            onChange={(e) => set("registryCode", e.target.value)}
            autoComplete="off"
          />
          <TextField label={t("partners.country")} hint={t("partners.countryHint")} value={form.country ?? ""} onChange={(e) => set("country", e.target.value.toUpperCase())} maxLength={2} autoComplete="off" />
          <SelectField
            label={t("order.currency")}
            value={form.currency}
            onChange={(e) => set("currency", e.target.value)}
            options={["UAH", "USD", "EUR", "PLN"].map((c) => ({ value: c, label: c }))}
          />
          <label className="check">
            <input type="checkbox" checked={form.vatPayer} onChange={(e) => set("vatPayer", e.target.checked)} />
            {t("partners.vatPayer")}
          </label>
          <label className="check">
            <input type="checkbox" checked={form.supplier} onChange={(e) => set("supplier", e.target.checked)} />
            {t("partners.supplier")}
          </label>
          <label className="check">
            <input type="checkbox" checked={form.customer} onChange={(e) => set("customer", e.target.checked)} />
            {t("partners.customer")}
          </label>
          <TextField label={t("partners.contacts")} value={form.contacts ?? ""} onChange={(e) => set("contacts", e.target.value)} />
          <Button type="submit" variant="primary" disabled={save.isPending || !form.name.trim()}>
            {partner ? t("order.save") : t("partners.create")}
          </Button>
        </form>
      </Plate>
      {partner ? (
        <div className="order-actions">
          <Button
            variant="ghost"
            disabled={archive.isPending}
            onClick={() =>
              archive.mutate(!partner.archived, {
                onSuccess: () => void queryClient.invalidateQueries({ queryKey: CATALOG }),
                onError: (err) => setNotice(errorNotice(err)),
              })
            }
          >
            {partner.archived ? t("catalog.restore") : t("catalog.archive")}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
